import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'agenda.appointment.cancel',
  category: 'agenda',
  title: 'Annuler un rendez-vous',
  intent: "Annuler un rendez-vous existant, avec notification au client et prise en compte du délai d'annulation.",
  context: {
    why: "L'annulation d'un rendez-vous n'est pas juste une suppression — c'est une action structurée qui déclenche plusieurs mécanismes automatiques pour préserver votre organisation et votre relation client. D'abord, le créneau annulé est immédiatement libéré et redevient réservable en ligne pour d'autres clients — vous ne perdez donc pas cette plage horaire, elle peut être re-remplie très vite. Ensuite, le client est automatiquement notifié de l'annulation, ce qui vous évite les allers-retours téléphoniques ou les rendez-vous manqués parce que le client n'était pas au courant. Enfin, les règles d'annulation configurées dans vos paramètres de réservation s'appliquent en cascade : si l'annulation a lieu avant le délai que vous avez fixé (typiquement 24 heures), le bon cadeau éventuellement utilisé est restitué au client automatiquement ; si l'annulation est hors délai, une pénalité peut être retenue selon la configuration de paiement de la prestation. Cette mécanique protège à la fois votre chiffre d'affaires et l'expérience client — les règles sont claires, transparentes et appliquées de manière cohérente. Sur les rendez-vous récurrents, vous pouvez choisir d'annuler uniquement cette occurrence ou toute la série ; et une annulation groupée permet de traiter plusieurs rendez-vous d'un coup, particulièrement utile pour un imprévu qui impacte toute une journée ou toute une semaine.",
    use_cases: [
      "Un client vous informe qu'il ne pourra pas honorer son rendez-vous : vous annulez proprement pour libérer le créneau et le notifier.",
      'Vous avez un imprévu personnel ou professionnel : vous annulez et le client est prévenu automatiquement.',
      'Un client annule avant le délai fixé (24 heures par exemple) : son bon cadeau éventuel est restitué automatiquement selon vos règles.',
      "Un client annule hors délai : la pénalité configurée s'applique selon le mode de paiement de la prestation.",
      "Vous devez annuler tous les rendez-vous d'une journée (formation imprévue, panne majeure) : utilisez l'annulation groupée.",
      "Vous annulez une occurrence d'un rendez-vous récurrent : décidez si vous annulez cette occurrence seule ou toute la série.",
    ],
  },
  required_scopes: ['agenda.appointment.delete'],
  prerequisites: [
    {
      ref: 'agenda.appointment.create',
      requirement: 'required',
      why: 'Un rendez-vous doit exister avant de pouvoir être annulé.',
    },
  ],
  ui_entry_points: [
    {
      route: '/agenda',
      element:
        'Clic sur un rendez-vous → « Annuler le rendez-vous » (bouton rouge de la cartouche Actions, RDV actif uniquement)',
    },
    { route: '/agenda', element: 'Sélection multiple de rendez-vous → annulation groupée (bulk)' },
  ],
  api: {
    primary_operation_id: 'AppointmentAppController_bulkCancelAppointments',
    related_operation_ids: ['AppointmentAppController_getAppointment', 'AppointmentAppController_getAppointmentDetail'],
  },
  steps: [
    '**Ouvrez le rendez-vous** : cliquez sur le RDV concerné.',
    '**Cliquez sur « Annuler le rendez-vous »** (bouton rouge dans la cartouche Actions) et confirmez.',
    '**Précisez le motif** (optionnel) si un champ est demandé — il sera transmis au client.',
    "**Validez** : le créneau se libère, le client est notifié et les règles d'annulation s'appliquent (restitution du bon éventuel, pénalité si hors délai).",
  ],
  gotchas: [
    '**Annulation par le pro vs demandée par le client** : dans les deux cas le créneau se libère et le client est prévenu. Le statut du RDV précise ensuite « Annulé par le professionnel » ou « Annulé par le client ».',
    "**Avant le délai d'annulation** (paramétré dans les paramètres de réservation) : le bon / la carte cadeau utilisé peut être **restitué** au client (selon la config).",
    '**Hors délai** : une pénalité ou un pourcentage peut être retenu selon la configuration de paiement de la prestation.',
    "**Créneau libéré** : le créneau annulé redevient réservable en ligne (sauf s'il était bloqué manuellement).",
    "**Motif transmis au client** : le motif d'annulation est repris dans la notification. Formulez-le proprement — Pulse peut aider si besoin.",
    "**Annulation groupée** : possibilité d'annuler plusieurs RDV d'un coup (utile pour un imprévu qui impacte toute une journée).",
    '**RDV récurrent** : préciser si vous annulez cette occurrence seulement ou toute la série.',
    "**Synchro Google Calendar** : si active, l'événement est supprimé côté Google en temps réel.",
    "**Droits** : les rôles déterminent qui peut annuler (uniquement ses RDV vs ceux de toute l'équipe).",
    "**Bouton absent d'un RDV déjà annulé** : « Annuler le rendez-vous » n'apparaît que sur un RDV actif — sur un RDV déjà annulé, seuls Reprogrammer et Modifier restent visibles.",
  ],
  related_features: [
    'agenda.appointment.create',
    'agenda.appointment.update',
    'agenda.appointment.status',
    'agenda.booking.settings',
    'sales.voucher.manage',
  ],
  demo_script: {
    duration_target_s: 35,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          "Annuler un rendez-vous, ce n'est pas juste le supprimer : le créneau se libère et votre client est prévenu automatiquement.",
      },
      {
        action: 'click',
        target: sidebarNavTestId('/agenda'),
        caption: "Direction l'Agenda, dans le menu latéral, où vivent tous vos rendez-vous.",
      },
      {
        action: 'wait_for',
        target: TEST_IDS.agenda_calendar,
        caption: 'Votre agenda s’affiche avec vos rendez-vous à venir.',
      },
      {
        action: 'click',
        target: TEST_IDS.agenda_event_card,
        caption: 'On ouvre le rendez-vous concerné pour agir dessus.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.appointment_cancel,
        caption: '« Annuler le rendez-vous » : ce bouton n’apparaît que sur un rendez-vous encore actif.',
      },
      {
        action: 'click',
        target: TEST_IDS.appointment_cancel_confirm,
        caption:
          'On confirme : le créneau redevient réservable en ligne et votre client est notifié, selon vos règles d’annulation.',
        capture: true,
      },
    ],
  },
});

// Verbatim Kaltouma (à conserver) :
// > Annuler un RDV : le créneau se libère, le client est notifié, et le délai
// > d'annulation s'applique (restitution d'un bon / pénalité). « Annuler le
// > rendez-vous » est un bouton rouge de la cartouche « Actions », présent
// > uniquement sur un RDV actif (absent d'un RDV déjà annulé).
